import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { UncontrolledTooltip } from 'reactstrap'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

import { AllAutomationExecutionLogsQuery, AutomationExecutionLogsQuery } from '../Queries'
import { RerunAutomationExecutionMutation } from '../Mutations'
import toTitleCase from '../../Functions/toTitleCase'
import ConfirmationPopup from '../../Material/ConfirmationPopup'

const PAGE_SIZE = 15
const SKELETON_ROW_COUNT = 15
const HISTORY_COLUMN_COUNT = 8

const parseJsonValue = (value) => {
    if (!value) return null
    if (typeof value === 'object') return value

    const candidates = [
        value,
        String(value)
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false')
            .replace(/'/g, '"'),
    ]

    for (const candidate of candidates) {
        try {
            const parsedValue = JSON.parse(candidate)
            if (typeof parsedValue === 'string' && parsedValue !== value) {
                return parseJsonValue(parsedValue)
            }
            return parsedValue
        } catch { /* try the next representation */ }
    }

    return null
}

const getEventType = (log) => {
    if (log.isNew) return { label: 'Created', icon: 'fa-plus', className: 'created' }
    if (log.isDeleted) return { label: 'Deleted', icon: 'fa-trash', className: 'deleted' }
    return { label: 'Updated', icon: null, className: 'updated' }
}

const getDatabaseId = (globalId) => {
    if (!globalId) return '—'
    try {
        return window.atob(globalId).split(':').pop()
    } catch {
        return globalId
    }
}

const getUserName = (user) => {
    if (!user) return null
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()
    return fullName || user.username || null
}

const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Empty'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') return JSON.stringify(value)
    if (typeof value === 'string' && moment(value, moment.ISO_8601, true).isValid()) {
        return moment(value).format('MMM D, YYYY · h:mm:ss A')
    }
    return String(value)
}

const parseActionResult = (result, actions) => {
    const rawResult = typeof result?.result === 'string' ? result.result : ''
    const failed = rawResult.includes('success=False') || result?.success === false
    const messageMatch = rawResult.match(/message=(.*)\)$/)
    const action = actions.find(({ node }) => (
        String(getDatabaseId(node.id)) === String(result?.action_id)
    ))?.node

    return {
        actionId: result?.action_id,
        actionName: toTitleCase(action?.actionType) || `Action #${result?.action_id || '—'}`,
        success: !failed,
        message: messageMatch?.[1] || result?.message || rawResult || (failed ? 'Action failed' : 'Action completed'),
    }
}

const HistoryTab = ({ automationId, showAutomation = false, embedded = false }) => {
    const [expandedLogId, setExpandedLogId] = useState(null)
    const [loadingMore, setLoadingMore] = useState(false)
    const [rerunningLogId, setRerunningLogId] = useState(null)
    const [rerunNotice, setRerunNotice] = useState(null)
    const [pendingRerunLog, setPendingRerunLog] = useState(null)
    const [rerunModalError, setRerunModalError] = useState(null)
    const query = automationId ? AutomationExecutionLogsQuery : AllAutomationExecutionLogsQuery
    const queryVariables = automationId
        ? { automation: automationId, first: PAGE_SIZE }
        : { first: PAGE_SIZE }
    const { data, loading, error, fetchMore, refetch } = useQuery(query, {
        variables: queryVariables,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })
    const [rerunExecution] = useMutation(RerunAutomationExecutionMutation)

    const connection = data?.allAutomationExecutionLogs
    const logs = connection?.edges || []
    const pageInfo = connection?.pageInfo
    const historyColumnCount = HISTORY_COLUMN_COUNT + (showAutomation ? 1 : 0)

    const loadMore = async () => {
        if (!pageInfo?.hasNextPage || loadingMore) return
        setLoadingMore(true)
        try {
            await fetchMore({
                variables: {
                    ...(automationId ? { automation: automationId } : {}),
                    first: PAGE_SIZE,
                    after: pageInfo.endCursor,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => ({
                    allAutomationExecutionLogs: {
                        ...fetchMoreResult.allAutomationExecutionLogs,
                        edges: [
                            ...previousResult.allAutomationExecutionLogs.edges,
                            ...fetchMoreResult.allAutomationExecutionLogs.edges,
                        ],
                    },
                }),
            })
        } finally {
            setLoadingMore(false)
        }
    }

    const handleRerun = async (log) => {
        setRerunningLogId(log.id)
        setRerunNotice(null)
        setRerunModalError(null)
        try {
            const { data: mutationData } = await rerunExecution({ variables: { input: { id: log.id } } })
            const result = mutationData?.rerunAutomationExecution
            if (!result?.ok) {
                throw new Error(result?.errors?.[0]?.messages?.[0] || 'The automation could not be re-run.')
            }

            await refetch({
                ...(automationId ? { automation: automationId } : {}),
                first: PAGE_SIZE,
                after: null,
            })
            setPendingRerunLog(null)
            setRerunNotice({ type: 'success', message: 'Automation re-run finished and a new execution was added.' })
        } catch (rerunError) {
            setRerunModalError(rerunError.message)
        } finally {
            setRerunningLogId(null)
        }
    }

    const closeRerunModal = () => {
        if (rerunningLogId) return
        setPendingRerunLog(null)
        setRerunModalError(null)
    }

    if (error) {
        return (
            <div className="wf-history-state wf-history-error">
                <span className="wf-history-state-icon"><i className="fa fa-exclamation-triangle" /></span>
                <h3>History could not be loaded</h3>
                <p>{error.message}</p>
            </div>
        )
    }

    return (
        <section className={`wf-history-view ${embedded ? 'wf-history-view-embedded' : ''}`}>
            {pendingRerunLog && (
                <ConfirmationPopup
                    open={!!pendingRerunLog}
                    message="Re-run this automation? Its actions will execute again and may resend messages or repeat external updates."
                    action="Re-run"
                    color="primary"
                    loading={rerunningLogId === pendingRerunLog.id}
                    error={rerunModalError}
                    handleClose={closeRerunModal}
                    confirmAction={() => handleRerun(pendingRerunLog)}
                />
            )}
            {rerunNotice && (
                <div className={`wf-rerun-notice ${rerunNotice.type}`} role="status">
                    <i className={`fa ${rerunNotice.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
                    <span>{rerunNotice.message}</span>
                    <button type="button" aria-label="Dismiss message" onClick={() => setRerunNotice(null)}>
                        <i className="fa fa-times" />
                    </button>
                </div>
            )}

            {!loading && logs.length === 0 ? (
                <div className="wf-history-state wf-history-empty">
                    <span className="wf-history-state-icon"><i className="fa fa-history" /></span>
                    <h3>No executions yet</h3>
                    <p>{automationId
                        ? 'Runs will appear here after this automation is triggered.'
                        : 'Runs will appear here after an automation is triggered.'}</p>
                </div>
            ) : (
                <div className="wf-history-card">
                    <div className="wf-history-table-wrap">
                        <table className="wf-history-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    {showAutomation && <th>Automation</th>}
                                    <th>Record</th>
                                    <th>Trigger</th>
                                    <th>Result</th>
                                    <th>Event</th>
                                    <th>Actions</th>
                                    <th>Run time</th>
                                    <th aria-label="Execution details"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && !connection ? (
                                    <SkeletonTheme baseColor="#e0e0e0" highlightColor="#f5f5f5">
                                        {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
                                            <tr key={`history-skeleton-${rowIndex}`} className="skeleton-row">
                                                {Array.from({ length: historyColumnCount }, (__, columnIndex) => (
                                                    <td key={columnIndex}>
                                                        <Skeleton height={20} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </SkeletonTheme>
                                ) : logs.map(({ node: log }, logIndex) => {
                                    const actions = log.actions?.edges || []
                                    const remainingActions = actions.slice(2)
                                    const remainingActionNames = remainingActions
                                        .map(({ node: action }) => toTitleCase(action?.actionType))
                                        .filter(Boolean)
                                        .join(', ')
                                    const remainingActionsTooltipId = `historyRemainingActions${logIndex}`
                                    const dirtyFields = parseJsonValue(log.dirtyFields)
                                    const metadata = parseJsonValue(log.metadata)
                                    const actionResults = Array.isArray(metadata?.actions_result)
                                        ? metadata.actions_result
                                        : []
                                    const actionResultDetails = actionResults.map((result) => parseActionResult(result, actions))
                                    const serializedMetadata = String(log.metadata || '')
                                    const executionFailed = actionResultDetails.some(({ success }) => !success)
                                        || serializedMetadata.includes('success=False')
                                        || serializedMetadata.includes('"success": false')
                                    const conditionGroups = log.conditionGroups?.edges || []
                                    const conditionCount = conditionGroups.reduce((count, { node: group }) => (
                                        count + (group.conditionSet?.edges?.length || 0)
                                    ), 0)
                                    const event = getEventType(log)
                                    const rerunCount = Number(metadata?.rerun?.count || 0)
                                    const rerunTooltip = rerunCount === 1
                                        ? 'Run has been replayed once'
                                        : `Run has been replayed ${rerunCount} times`
                                    const executionId = log.pk || getDatabaseId(log.id)
                                    const replayedFromId = metadata?.replayed_from_execution_log_id
                                    const initiatedBy = getUserName(log.createdBy)
                                        || metadata?.rerun_by_name
                                        || metadata?.rerun_by_username
                                        || (metadata?.rerun_by_user_id ? `User #${metadata.rerun_by_user_id}` : 'System trigger')
                                    const lastReplayBy = metadata?.rerun?.last_rerun_by_name
                                        || metadata?.rerun?.last_rerun_by_username
                                        || (metadata?.rerun?.last_rerun_by_user_id
                                            ? `User #${metadata.rerun.last_rerun_by_user_id}`
                                            : 'Unknown user')
                                    const expanded = expandedLogId === log.id

                                    return (
                                        <React.Fragment key={log.id}>
                                            <tr className={expanded ? 'wf-history-row-expanded' : ''}>
                                                <td className="wf-history-log-id">#{executionId}</td>
                                                {showAutomation && (
                                                    <td className="wf-history-automation-cell">
                                                        <Link
                                                            to={`/automations/${log.automation?.id}#history`}
                                                            title={toTitleCase(log.automation?.name)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <span className="wf-history-automation-icon"><i className="fa fa-code-fork" /></span>
                                                            <strong>{toTitleCase(log.automation?.name) || 'Automation'}</strong>
                                                        </Link>
                                                    </td>
                                                )}
                                                <td>
                                                    <div className="wf-history-record">
                                                        <strong>{toTitleCase(log.contentType?.model) || 'Record'}</strong>
                                                        <small>#{log.objectId || '—'}</small>
                                                    </div>
                                                </td>
                                                <td>{toTitleCase(log.trigger?.triggerType) || '—'}</td>
                                                <td>
                                                    <div className="wf-history-result-wrap">
                                                        <span className={`wf-run-result ${executionFailed ? 'failed' : 'completed'}`}>
                                                            <i className={`fa ${executionFailed ? 'fa-exclamation' : 'fa-check'}`} />
                                                            {executionFailed ? 'Failed' : 'Completed'}
                                                        </span>
                                                        {rerunCount > 0 && (
                                                            <span className="wf-rerun-marker" title={rerunTooltip} aria-label={rerunTooltip}>
                                                                <i className="fa fa-refresh" />
                                                                {rerunCount > 1 && <small>{rerunCount}</small>}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`wf-event-badge ${event.className}`}>
                                                        {event.icon && <i className={`fa ${event.icon}`} />}
                                                        {event.label}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="wf-history-actions">
                                                        {actions.slice(0, 2).map(({ node: action }) => (
                                                            <span key={action.id} title={toTitleCase(action.actionType)}>
                                                                {toTitleCase(action.actionType)}
                                                            </span>
                                                        ))}
                                                        {remainingActions.length > 0 && (
                                                            <>
                                                                <em
                                                                    id={remainingActionsTooltipId}
                                                                    tabIndex={0}
                                                                    aria-label={`${remainingActions.length} more ${remainingActions.length === 1 ? 'action' : 'actions'}: ${remainingActionNames}`}
                                                                >
                                                                    +{remainingActions.length}
                                                                </em>
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={remainingActionsTooltipId}
                                                                >
                                                                    {remainingActionNames}
                                                                </UncontrolledTooltip>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="wf-history-time" title={moment(log.dateAdded).format('MMMM D, YYYY [at] h:mm:ss A')}>
                                                        <strong>{moment(log.dateAdded).fromNow()}</strong>
                                                        <small>{moment(log.dateAdded).format('MMM D, YYYY · h:mm A')}</small>
                                                    </div>
                                                </td>
                                                <td className="wf-history-expand-cell">
                                                    <div className="wf-history-row-actions">
                                                        <button
                                                            type="button"
                                                            className="wf-rerun-button"
                                                            title={log.isDeleted
                                                                ? 'The target record was deleted and cannot be re-run'
                                                                : actions.length === 0
                                                                    ? 'This execution has no recorded actions to re-run'
                                                                    : 'Re-run this automation'}
                                                            aria-label="Re-run this automation"
                                                            disabled={log.isDeleted || actions.length === 0 || rerunningLogId === log.id}
                                                            onClick={() => {
                                                                setRerunModalError(null)
                                                                setPendingRerunLog(log)
                                                            }}
                                                        >
                                                            <i className={`fa ${rerunningLogId === log.id ? 'fa-circle-o-notch fa-spin' : 'fa-repeat'}`} />
                                                            <span>{rerunningLogId === log.id ? 'Running' : 'Re-run'}</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="wf-expand-button"
                                                            aria-label={expanded ? 'Hide execution details' : 'Show execution details'}
                                                            aria-expanded={expanded}
                                                            onClick={() => setExpandedLogId(expanded ? null : log.id)}
                                                        >
                                                            <i className={`fa fa-chevron-${expanded ? 'up' : 'down'}`} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expanded && (
                                                <tr className="wf-history-detail-row">
                                                    <td colSpan={historyColumnCount}>
                                                        <div className="wf-history-details">
                                                            <section className="wf-run-detail-summary">
                                                                <div className="wf-run-detail-summary-header">
                                                                    <span className="wf-run-detail-icon"><i className="fa fa-history" /></span>
                                                                    <div>
                                                                        <span>Execution</span>
                                                                        <strong>Run #{executionId}</strong>
                                                                    </div>
                                                                    <span className={`wf-run-result ${executionFailed ? 'failed' : 'completed'}`}>
                                                                        <i className={`fa ${executionFailed ? 'fa-exclamation' : 'fa-check'}`} />
                                                                        {executionFailed ? 'Failed' : 'Completed'}
                                                                    </span>
                                                                    {replayedFromId && (
                                                                        <span className="wf-manual-replay-badge"><i className="fa fa-repeat" /> Manual replay</span>
                                                                    )}
                                                                </div>

                                                                <div className="wf-run-detail-grid">
                                                                    <div><span>Automation</span><strong>{toTitleCase(log.automation?.name) || 'Automation'}</strong></div>
                                                                    <div><span>Target record</span><strong>{toTitleCase(log.contentType?.model) || 'Record'} #{log.objectId || '—'}</strong></div>
                                                                    <div><span>Trigger and event</span><strong>{toTitleCase(log.trigger?.triggerType) || '—'} · {event.label}</strong></div>
                                                                    <div><span>Executed at</span><strong>{moment(log.dateAdded).format('MMM D, YYYY · h:mm:ss A')}</strong></div>
                                                                    <div><span>Workflow steps</span><strong>{conditionCount} {conditionCount === 1 ? 'condition' : 'conditions'} · {actions.length} {actions.length === 1 ? 'action' : 'actions'}</strong></div>
                                                                    <div><span>Initiated by</span><strong>{initiatedBy}</strong></div>
                                                                </div>

                                                                {(replayedFromId || rerunCount > 0) && (
                                                                    <div className="wf-replay-context">
                                                                        {replayedFromId && (
                                                                            <p><i className="fa fa-reply" /> This run was manually replayed from execution #{replayedFromId} by {initiatedBy}.</p>
                                                                        )}
                                                                        {rerunCount > 0 && (
                                                                            <p><i className="fa fa-refresh" /> This execution has been replayed {rerunCount === 1 ? 'once' : `${rerunCount} times`}. The latest replay was {metadata.rerun.last_rerun_at ? moment(metadata.rerun.last_rerun_at).fromNow() : 'recently'} by {lastReplayBy}.</p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </section>

                                                            <div className="wf-history-detail-grid">
                                                                <div className="wf-history-detail-block">
                                                                    <h4><i className="fa fa-exchange" /> Changed fields <span>{dirtyFields ? Object.keys(dirtyFields).length : 0}</span></h4>
                                                                    {dirtyFields && Object.keys(dirtyFields).length > 0 ? (
                                                                        <div className="wf-changed-fields-list">
                                                                            {Object.entries(dirtyFields).map(([field, value]) => (
                                                                                <div key={field}>
                                                                                    <span>{toTitleCase(field)}</span>
                                                                                    <strong title={formatDetailValue(value)}>{formatDetailValue(value)}</strong>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : <p>No changed-field data was recorded.</p>}
                                                                </div>

                                                                <div className="wf-history-detail-block">
                                                                    <h4><i className="fa fa-list-alt" /> Action results <span>{actionResultDetails.length}</span></h4>
                                                                    {actionResultDetails.length > 0 ? (
                                                                        <div className="wf-action-results-list">
                                                                            {actionResultDetails.map((result, index) => (
                                                                                <div className="wf-action-result" key={`${result.actionId || 'action'}-${index}`}>
                                                                                    <div>
                                                                                        <strong>{result.actionName}</strong>
                                                                                        <span className={result.success ? 'success' : 'failed'}>
                                                                                            <i className={`fa ${result.success ? 'fa-check' : 'fa-exclamation'}`} />
                                                                                            {result.success ? 'Completed' : 'Failed'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <p>{result.message}</p>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : <p>No action-result details were recorded.</p>}
                                                                </div>

                                                                <div className="wf-history-detail-block wf-conditions-detail-block">
                                                                    <h4><i className="fa fa-filter" /> Conditions used <span>{conditionCount}</span></h4>
                                                                    {conditionGroups.length > 0 ? (
                                                                        <div className="wf-condition-groups-list">
                                                                            {conditionGroups.map(({ node: group }, groupIndex) => (
                                                                                <div className="wf-condition-group-summary" key={group.id}>
                                                                                    <div className="wf-condition-group-heading">
                                                                                        <strong>Group {groupIndex + 1}</strong>
                                                                                        <span>Match {group.logicalOperator === 'AND' ? 'all' : 'any'}</span>
                                                                                    </div>
                                                                                    {group.conditionSet?.edges?.length > 0 ? (
                                                                                        <ul>
                                                                                            {group.conditionSet.edges.map(({ node: condition }) => (
                                                                                                <li key={condition.id}>
                                                                                                    <span>{toTitleCase(condition.version)} {toTitleCase(condition.fieldName)}</span>
                                                                                                    <em>{toTitleCase(condition.conditionType)}</em>
                                                                                                    <strong>{formatDetailValue(condition.conditionValue)}</strong>
                                                                                                </li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    ) : <p>No individual conditions were recorded in this group.</p>}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : <p>No conditions were associated with this execution.</p>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {pageInfo?.hasNextPage && (
                        <div className="wf-history-footer">
                            <button type="button" onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? <span className="wf-history-spinner small"></span> : <i className="fa fa-angle-down" />}
                                {loadingMore ? 'Loading...' : 'Load more executions'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    )
}

export default HistoryTab
